import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  HiBell,
  HiCake,
  HiExclamationTriangle,
  HiClock,
  HiTrophy,
  HiDocumentCheck,
  HiCalendarDays,
  HiGift,
} from "react-icons/hi2";
// @ts-ignore
import { IconWrapper } from "@viuti/recursos";
import styles from "./NotificationBell.module.css";

type Severity = "info" | "success" | "warning" | "critical";
type NotificationType =
  | "BIRTHDAY"
  | "STOCK_LOW"
  | "PLAN_EXPIRING"
  | "GOAL_ACHIEVED"
  | "CERT_SYNCED"
  | "CERT_SYNC_DELAYED"
  | "CERT_EXPIRING"
  | "FE_REJECTED"
  | "VOID_REJECTED"
  // #12: anulación con reintegros (stock, paquetes, saldos) trabados
  | "VOID_EFFECTS_STUCK"
  | "RESERVATION_MODIFIED_BY_OTHER"
  | "REFERRAL_REWARD";

interface Notification {
  key: string;
  type: NotificationType;
  title: string;
  body: string;
  severity: Severity;
  actionUrl: string;
  createdAt: string;
}

const TYPE_ICON: Record<NotificationType, any> = {
  BIRTHDAY: HiCake,
  STOCK_LOW: HiExclamationTriangle,
  PLAN_EXPIRING: HiClock,
  GOAL_ACHIEVED: HiTrophy,
  CERT_SYNCED: HiDocumentCheck,
  CERT_SYNC_DELAYED: HiClock,
  CERT_EXPIRING: HiClock,
  FE_REJECTED: HiExclamationTriangle,
  VOID_REJECTED: HiExclamationTriangle,
  VOID_EFFECTS_STUCK: HiExclamationTriangle,
  RESERVATION_MODIFIED_BY_OTHER: HiCalendarDays,
  REFERRAL_REWARD: HiGift,
};

const TYPE_COLOR: Record<NotificationType, string> = {
  BIRTHDAY: "#45348E",
  STOCK_LOW: "#D97706",
  PLAN_EXPIRING: "#B91C1C",
  GOAL_ACHIEVED: "#1A8754",
  CERT_SYNCED: "#1A8754",
  CERT_SYNC_DELAYED: "#D97706",
  CERT_EXPIRING: "#B91C1C",
  FE_REJECTED: "#D97706",
  VOID_REJECTED: "#B91C1C",
  VOID_EFFECTS_STUCK: "#B91C1C",
  RESERVATION_MODIFIED_BY_OTHER: "#45348E",
  REFERRAL_REWARD: "#16A34A",
};

const POLLING_MS = 5 * 60 * 1000;

const NotificationBell = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const token = () => localStorage.getItem("access_token") || "";

  const fetchNotifications = async () => {
    try {
      const r = await fetch("/api/v2/notifications", {
        headers: { Authorization: "Bearer " + token() },
      });
      if (!r.ok) return;
      const j = await r.json();
      setNotifications(j?.data || []);
    } catch {
      /* silencioso */
    }
  };

  useEffect(() => {
    fetchNotifications();
    const id = setInterval(fetchNotifications, POLLING_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNotificationClick = async (n: Notification) => {
    setIsOpen(false);
    setNotifications((prev) => prev.filter((x) => x.key !== n.key));
    try {
      await fetch("/api/v2/notifications/dismiss", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token(),
        },
        body: JSON.stringify({ key: n.key }),
      });
    } catch {
      /* silencioso */
    }
    navigate(n.actionUrl);
  };

  const handleDismissOnly = async (
    e: React.MouseEvent,
    n: Notification,
  ) => {
    e.stopPropagation();
    setNotifications((prev) => prev.filter((x) => x.key !== n.key));
    try {
      await fetch("/api/v2/notifications/dismiss", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token(),
        },
        body: JSON.stringify({ key: n.key }),
      });
    } catch {
      /* silencioso */
    }
  };

  const count = notifications.length;

  return (
    <div className={styles.container} ref={dropdownRef} data-testid="notification-bell">
      <button
        className={styles.trigger}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Notificaciones"
        data-testid="notification-bell-button"
      >
        <IconWrapper icon={HiBell} size={18} color="#45348E" />
        {count > 0 && (
          <span className={styles.badge} data-testid="notification-badge">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>
      {isOpen && (
        <div className={styles.dropdown} data-testid="notification-dropdown">
          <div className={styles.header}>
            <span>Notificaciones</span>
            {count > 0 && <span className={styles.headerCount}>{count}</span>}
          </div>
          {count === 0 ? (
            <div className={styles.empty}>No tienes notificaciones nuevas.</div>
          ) : (
            <div className={styles.list}>
              {notifications.map((n) => (
                <div
                  key={n.key}
                  className={`${styles.item} ${styles[`severity_${n.severity}`]}`}
                  data-testid={`notification-${n.type}`}
                  onClick={() => handleNotificationClick(n)}
                >
                  <span className={styles.icon}>
                    <IconWrapper
                      icon={TYPE_ICON[n.type]}
                      size={20}
                      color={TYPE_COLOR[n.type]}
                    />
                  </span>
                  <div className={styles.body}>
                    <strong className={styles.title}>{n.title}</strong>
                    <span className={styles.text}>{n.body}</span>
                  </div>
                  <button
                    className={styles.dismissBtn}
                    onClick={(e) => handleDismissOnly(e, n)}
                    aria-label="Descartar"
                    data-testid={`notification-dismiss-${n.type}`}
                  >
                    {"×"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
